import { FC, ChangeEvent, useState, useRef, useEffect } from 'react';
import './TextArea.scss';

interface TextInputProps {
  name: string;
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  errorMessage?: string;
  isError?: boolean;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>, name: any) => void;
  isDisabled?: boolean;
  title?: string;
  rows?: number;
  enableTagHighlighting?: boolean;
  onTagDetection?: (value: string, cursorPosition: number) => void;
  textareaRef?: React.RefObject<HTMLTextAreaElement | HTMLDivElement | null>;
  onTagDetect?: (query: string, position: { top: number; left: number }) => void;
  onTagClose?: () => void;
}

const TextInput: FC<TextInputProps> = ({
  name,
  value,
  defaultValue,
  placeholder,
  errorMessage,
  isError,
  onChange,
  isDisabled,
  title,
  rows = 4,
  enableTagHighlighting = false,
  onTagDetection,
  textareaRef,
  onTagDetect,
  onTagClose
}) => {
  const maxRows = 4;
  const [currentRows, setCurrentRows] = useState(1);
  const [highlightedValue, setHighlightedValue] = useState('');
  const textareaRefInternal = useRef<HTMLTextAreaElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const actualTextareaRef = textareaRef || textareaRefInternal;

  const highlightTaggedUsers = (text: string): string => {
    if (!text) return '';
    return text.replace(/@(\w+)/g, '<span class="tagged-user-highlight">@$1</span>');
  };

  useEffect(() => {
    if (enableTagHighlighting && value) {
      setHighlightedValue(highlightTaggedUsers(value));
    }
  }, [value, enableTagHighlighting]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const textValue = e.target.value || '';
    if (onChange) onChange(e, name);

    // Calculate line count by splitting on newline characters
    const lineCount = textValue.split('\n').length;
    // Clamp the row count between 1 and maxRows
    const newRowCount = Math.min(lineCount, maxRows);
    setCurrentRows(newRowCount);

    // Update highlighted value for tag highlighting
    if (enableTagHighlighting) {
      setHighlightedValue(highlightTaggedUsers(textValue));
    }

    // Call onTagDetection if provided
    if (onTagDetection) {
      const cursorPosition = e.target.selectionStart || 0;
      onTagDetection(textValue, cursorPosition);
    }

    if (onTagDetect) {
      const cursorPosition = e.target.selectionStart || 0;
      const textBeforeCursor = textValue.slice(0, cursorPosition);
      const mentionMatch = textBeforeCursor.match(/(?:^|\s)@([^\s@]*)$/);

      if (mentionMatch) {
        onTagDetect(mentionMatch[1] || '', { top: 0, left: 0 });
      } else if (onTagClose) {
        onTagClose();
      }
    }
  };

  // Use layered approach for tag highlighting
  if (enableTagHighlighting) {
    return (
      <div className='text-area-input-wrapper'>
        {title && (
          <span
            className={`text-input-title ${isDisabled ? 'disabled-title' : ''}`}
          >
            {title}
          </span>
        )}
        <div className='textarea-highlight-wrapper'>
          {/* Highlighted background layer */}
          <div
            ref={highlightRef}
            className='textarea-highlight-layer'
            dangerouslySetInnerHTML={{ __html: highlightedValue }}
            aria-hidden="true"
          />
          {/* Actual textarea */}
          <textarea
            ref={actualTextareaRef as React.RefObject<HTMLTextAreaElement>}
            name={name}
            rows={rows ?? currentRows}
            placeholder={placeholder}
            defaultValue={defaultValue}
            value={value}
            onChange={handleChange}
            className={`textarea-input textarea-overlay ${isError ? 'text-input-error' : ''}`}
            disabled={isDisabled}
          />
        </div>
        {isError && errorMessage && (
          <div className='error-message'>{errorMessage}</div>
        )}
      </div>
    );
  }

  return (
    <div className='text-area-input-wrapper'>
      {title && (
        <span
          className={`text-input-title ${isDisabled ? 'disabled-title' : ''}`}
        >
          {title}
        </span>
      )}
      <textarea
        ref={actualTextareaRef as React.RefObject<HTMLTextAreaElement>}
        name={name}
        rows={rows ?? currentRows}
        placeholder={placeholder}
        defaultValue={defaultValue}
        value={value}
        onChange={handleChange}
        className={`textarea-input ${isError ? 'text-input-error' : ''}`}
        disabled={isDisabled}
      />
      {isError && errorMessage && (
        <div className='error-message'>{errorMessage}</div>
      )}
    </div>
  );
};

export default TextInput;
